/**
 * CustomersView - Full-page view for customers management
 */

import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  Fab,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Add, Refresh, Search } from "@mui/icons-material";
import { useDeviceType } from "../../leads/hooks/useDeviceType";
import CustomerList from "../components/CustomerList";
import CustomerEditModal from "../components/CustomerEditModal";
import { getCustomers } from "../services/customerService";
import {
  enhanceCustomers,
  formatPhoneDisplay,
  getCustomerProfilePath,
} from "../utils/customerUtils";
import { logError } from "@/shared/utils/logger.js";

/**
 * CustomersView Component
 */
const CustomersView = () => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();

  // State
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCustomers({}, 500, 0); // Get more for filtering
      if (result.success) {
        const enhanced = enhanceCustomers(result.data || []);
        setCustomers(enhanced);
        setFilteredCustomers(enhanced);
      } else {
        setError(result.error || "Failed to fetch customers");
        logError("Failed to fetch customers", {
          error: result.error,
        });
      }
    } catch (err) {
      const errorMessage = err.message || "Failed to fetch customers";
      setError(errorMessage);
      logError("Error fetching customers", {
        error: err.message,
        stack: err.stack,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Filter customers based on search
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredCustomers(customers);
      return;
    }

    const searchLower = searchText.toLowerCase();
    const filtered = customers.filter((customer) => {
      const name = (customer.name || "").toLowerCase();
      const phone = formatPhoneDisplay(customer.phone) || "";
      const email = (customer.email || "").toLowerCase();

      return (
        name.includes(searchLower) ||
        phone.includes(searchLower) ||
        email.includes(searchLower)
      );
    });

    setFilteredCustomers(filtered);
  }, [searchText, customers]);

  // Handlers
  const handleViewCustomer = useCallback(
    (customer) => {
      const profilePath = getCustomerProfilePath(customer);
      if (!profilePath) {
        logError("Cannot navigate to customer profile without an ID", {
          customer,
        });
        return;
      }
      navigate(profilePath);
    },
    [navigate]
  );

  const handleCreateCustomer = useCallback(() => {
    setEditCustomer({});
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditCustomer(null);
  }, []);

  const handleCustomerUpdated = useCallback(() => {
    setEditCustomer(null);
    fetchCustomers();
    setNotification({
      open: true,
      message: "Customer updated successfully",
      severity: "success",
    });
  }, [fetchCustomers]);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Error: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={isMobile ? 2 : 3}>
      {/* Header */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
        gap={2}
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Customers
        </Typography>

        {/* Search + Actions */}
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          gap={2}
          alignItems={isMobile ? "stretch" : "center"}
          sx={{ width: isMobile ? "100%" : "auto" }}
        >
          {/* Search */}
          <TextField
            placeholder="Search customers..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: isMobile ? "100%" : 300,
            }}
          />

          {/* Actions */}
          {!isMobile && (
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchCustomers}
                disabled={loading}
                size="small"
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateCustomer}
              >
                New Customer
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Customer List */}
      <CustomerList
        customers={filteredCustomers}
        loading={loading}
        onViewCustomer={handleViewCustomer}
        onRefresh={fetchCustomers}
      />

      {/* Floating Action Button (Mobile) */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add customer"
          onClick={handleCreateCustomer}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Edit Modal */}
      {editCustomer !== null && (
        <CustomerEditModal
          customer={editCustomer}
          open={editCustomer !== null}
          onClose={handleCloseEditModal}
          onSuccess={handleCustomerUpdated}
        />
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomersView;

